import { Heart, HandHeart, PawPrint } from "@phosphor-icons/react";
import { FadeUp, SectionLabel, Stat } from "@/components/Reveal";
import { PARTNERS_COLLAGE, WOMEN_OWNED, NONPROFIT_1, NONPROFIT_2, JIMMYFUND_GROUP, PARTNERS, NONPROFITS } from "@/data/content";

export default function Partners() {
  return (
    <div data-testid="page-partners" className="pb-10">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 md:pt-28">
        <SectionLabel className="mb-8">Partners &amp; Impact</SectionLabel>
        <FadeUp>
          <h1 className="font-serif text-[12vw] md:text-[8vw] leading-[0.92] tracking-tight max-w-5xl">
            Flowing with <span className="font-serif-italic text-teal">purpose.</span>
          </h1>
        </FadeUp>
        <FadeUp delay={0.15} className="mt-10 max-w-2xl font-light text-lg leading-relaxed text-charcoal/75">
          Every Flowtastics class lifts another woman's work or a cause we care about. These partnerships are the heartbeat of our community.
        </FadeUp>
      </section>

      {/* Woman owned partners */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-28 md:mt-40" data-testid="partners-women-owned">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <FadeUp className="lg:col-span-5">
            <SectionLabel className="mb-6">Women-Owned</SectionLabel>
            <h2 className="font-serif text-5xl md:text-6xl tracking-tight leading-[1.02]">
              Brands built by <span className="font-serif-italic text-teal">women</span>, for women.
            </h2>
            <p className="font-light text-lg leading-relaxed text-charcoal/75 mt-8">
              We collaborate with women-owned businesses across fashion, fitness, art, and movement — amplifying their craft through every event.
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-4">
              {PARTNERS.map((p, i) => (
                <FadeUp key={p.name} delay={i * 0.06} className="border border-charcoal/15 rounded-3xl p-7 hover:border-teal hover:bg-blush/20 transition-all duration-500 group" data-testid={`partner-card-${p.name.replace(/[^a-z]/gi, "").toLowerCase()}`}>
                  <div className="text-[10px] uppercase tracking-editorial text-gold mb-3">Partner · 0{i + 1}</div>
                  <h3 className="font-serif text-3xl">{p.name}</h3>
                  <p className="font-light text-charcoal/70 mt-3 text-sm">{p.note}</p>
                </FadeUp>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Partners collage */}
      <section className="max-w-[1000px] mx-auto px-6 lg:px-10 mt-24">
        <FadeUp className="aspect-square md:aspect-[5/4] rounded-3xl overflow-hidden bg-blush/30 border-2 border-blush">
          <img src={WOMEN_OWNED} alt="Partners collage" className="w-full h-full object-contain p-4" />
        </FadeUp>
      </section>

      {/* Nonprofits */}
      <section className="bg-blush/30 grain py-28 md:py-36 mt-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-16">
          <FadeUp>
            <div className="rounded-3xl overflow-hidden">
              <img src={JIMMYFUND_GROUP} alt="Flow for a Cause — Jimmy Fund rooftop community" className="w-full h-auto block" />
            </div>
            <p className="mt-5 text-center text-[11px] uppercase tracking-editorial text-charcoal/60">
              Flow for a Cause · In support of the Jimmy Fund · Triple C's Dana-Farber Foundation
            </p>
          </FadeUp>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <FadeUp className="lg:col-span-5">
            <SectionLabel className="mb-6">Nonprofits &amp; Charities</SectionLabel>
            <h2 className="font-serif text-5xl md:text-6xl tracking-tight leading-[1.02]">
              Movement<br /><span className="font-serif-italic text-teal">as service.</span>
            </h2>
            <p className="font-light text-lg leading-relaxed text-charcoal/75 mt-8 max-w-md">
              Flowtastics Flows with Purpose. Specific event series donate 100% of their profits directly to mission-driven organizations.
            </p>
            <div className="rounded-3xl overflow-hidden mt-10 bg-blush/30 border-2 border-blush">
              <img src={NONPROFIT_2} alt="" className="w-full h-auto block p-2" />
            </div>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-7 space-y-6">
            {NONPROFITS.map((n, i) => (
              <FadeUp key={n.name} delay={i * 0.08} className="bg-cream rounded-3xl p-8 md:p-10 group" data-testid={`nonprofit-card-${i}`}>
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <div className="text-[10px] uppercase tracking-editorial text-gold mb-3">Cause · 0{i + 1}</div>
                    <h3 className="font-serif text-3xl md:text-4xl">{n.name}</h3>
                    <p className="font-light text-charcoal/70 mt-3">{n.in_support_of}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-serif text-3xl text-teal">{n.raised}</div>
                    <div className="text-[10px] uppercase tracking-editorial text-charcoal/50 mt-1">Raised</div>
                  </div>
                </div>
              </FadeUp>
            ))}
            <FadeUp className="bg-charcoal text-cream rounded-3xl p-8 md:p-10">
              <Heart size={24} weight="light" className="text-blush mb-4" />
              <p className="font-serif italic text-2xl leading-snug">All "Aerial Silk" profits support The Circus Complex Co. Nonprofit. "Frenchie Flow" profits support the Animal Rescue League of Boston. September flows give to the Jimmy Fund.</p>
            </FadeUp>
          </FadeUp>
        </div>
      </section>

      {/* Become a partner */}
      <section className="max-w-[1100px] mx-auto px-6 mt-28 md:mt-40 text-center" data-testid="partners-cta">
        <FadeUp>
          <SectionLabel className="mb-6 inline-flex justify-center">Collaborate</SectionLabel>
          <h2 className="font-serif text-5xl md:text-6xl tracking-tight">
            Bring your <span className="font-serif-italic text-teal">art</span> to the mat.
          </h2>
          <p className="font-light text-lg leading-relaxed text-charcoal/75 mt-8 max-w-xl mx-auto">
            Are you a woman-owned brand, artist, or nonprofit? We're always looking for the next meaningful collaboration.
          </p>
          <a href="/contact" className="inline-flex items-center gap-2 mt-10 rounded-full bg-charcoal text-cream px-7 py-4 text-[11px] uppercase tracking-editorial hover:bg-teal transition-all" data-testid="partners-cta-button">
            Start a conversation
          </a>
        </FadeUp>
      </section>
    </div>
  );
}
